import {Module} from '@gongxigames/framework/slot/fragments/module/Module';
import {GlobalEventsMapForBackwardCompatibility} from '@gongxigames/framework/slot/events';
import {Timer} from '@gongxigames/framework/core';
import {INACTIVITY_TIMER, INACTIVITY_TIMER_WITH_HOME_BUTTON} from '@gongxigames/framework/slot/configs/messages';
import {ReelsEvents} from '@gongxigames/framework/slot/modules/reels/ReelsEvents';
import {PickBonusEvents} from '@gongxigames/framework/slot/modules/pick/PickBonusEvents';
import {SelectBonusEvents} from '@gongxigames/framework/slot/modules/selectBonus/SelectBonusEvents';
import {BuyBonusPopUpEvents} from '@gongxigames/framework/slot/modules/buyBonus/buyBonusPopUp/BuyBonusPopUpEvents';
import {GenericPopupEvents, GenericPopupType} from '@gongxigames/framework/slot/modules/genericPopup';

const MS_IN_MINUTE = 60000;

export class InactivityTimerModule extends Module<null, GlobalEventsMapForBackwardCompatibility> {
    public static readonly NAME: symbol = Symbol.for('InactivityTimerModule');

    protected _timer?: Timer;
    protected _timeout = 0;
    protected _timeWhenTabBecomeInvisible = 0;
    protected _isShown = false;
    protected _featurePopUp = false;

    public init(): void {
        if (this._settings.get('inactivityTimer')) {
            this._timeout = this._settings.get('inactivityTimer') * MS_IN_MINUTE;

            super.init();
        }
    }

    protected setupEvents(): void {
        super.setupEvents();

        this.on('General.Screen.BECAME_VISIBLE', this.onBecameVisible);
        this.on('General.Screen.BECAME_INVISIBLE', this.onBecameInvisible);

        this.onAll([
            'General.Game.STARTED',
            ReelsEvents.REELS_SPINNING_STARTED,
            PickBonusEvents.PICK,
            SelectBonusEvents.SELECT,
            BuyBonusPopUpEvents.BUY_BUTTON_CLICKED
        ], this.startTimer);

        this.on(GenericPopupEvents.INTRO_STARTED, this.onGenericPopupShow.bind(this));
        this.on(GenericPopupEvents.POPUP_CLOSED, this.onGenericPopupClosed.bind(this));
    }

    protected onGenericPopupClosed(eventType: Symbol, popupType: Symbol): void {
        switch (popupType) {
            case GenericPopupType.FREESPIN_OUTRO:
            case GenericPopupType.FREESPIN_OUTRO_NO_WIN:
                this.startTimer();
                break;
            case GenericPopupType.FREESPIN_RETRIGGER:
            case GenericPopupType.RESPIN_INTRO:
            case GenericPopupType.RESPIN_OUTRO:
                this._featurePopUp = false;
                this.startTimer();
                break;
            default:
                break;
        }
    }

    protected onGenericPopupShow(eventType: Symbol, popupType: Symbol): void {
        switch (popupType) {
            case GenericPopupType.FREESPIN_INTRO:
            case GenericPopupType.FREESPIN_OUTRO:
                this.startTimer();
                break;
            case GenericPopupType.FREESPIN_RETRIGGER:
            case GenericPopupType.RESPIN_INTRO:
            case GenericPopupType.RESPIN_OUTRO:
                this._featurePopUp = true;
                this.startTimer();
                break;
            default:
                break;
        }
    }

    protected startTimer(): void {
        this.clearTimer();
        this._timer = this.updateManager.addTimer(this.showPopup.bind(this), this._timeout, true);
    }

    protected clearTimer(): void {
        this._timer && this._timer.stop();
        delete this._timer;
    }

    protected showPopup(): void {
        if (this.shouldNotShowInactivityPopup()) {
            return;
        }
        const isHomeUrlExist = this._settings.has('homeUrl');
        const message = isHomeUrlExist ? INACTIVITY_TIMER_WITH_HOME_BUTTON : INACTIVITY_TIMER;

        this._eventManager.emit('General.Message.SHOW', message);
        this._isShown = true;

        this._system.freeze();
    }

    protected shouldNotShowInactivityPopup(): boolean {
        const freeGamesIndex = this.logic.getFreeGamesIndex();
        const freeGamesCount = this.logic.getFreeGamesCount();
        const freeGamesTotalCount = this.logic.getFreeGamesTotalCount();
        const firecatcherRespinCount = this.logic.getFirecatcherRespinCount();
        const isBonusOnFreeGames = this.logic.spinData && this.logic.spinData.bonusId && this.logic.isBonusOnFreeGames();
        const inFreeGames =
                freeGamesIndex !== -1 &&
                freeGamesCount !== freeGamesTotalCount &&
                freeGamesCount !== 0 &&
                !isBonusOnFreeGames &&
                !this._featurePopUp &&
                firecatcherRespinCount <= 0;

        return (this._timeWhenTabBecomeInvisible === 0 && inFreeGames) || this._isShown;
    }

    protected onBecameVisible(): void {
        if (this._timer) {
            const invisibilityTime = Date.now() - this._timeWhenTabBecomeInvisible;

            this._timer.update(invisibilityTime);
            this._timeWhenTabBecomeInvisible = 0;
        }
    }

    protected onBecameInvisible(): void {
        if (this._timer) {
            this._timeWhenTabBecomeInvisible = Date.now();
        }
    }
}
